import type { ShipmentStatusGroup } from '@/api/types/common';
import { type OrderStatusFilter, ORDER_SHIPMENT_STATUS_NEW } from '@/api/types/order-shipments';

/**
 * Colour-coding groups in display order. Mirrors the backend `ShipmentStatusGroup`
 * enum; used for badge tints and for grouping the event-code filter dropdown.
 */
export const STATUS_GROUPS: ShipmentStatusGroup[] = [
  'new',
  'created',
  'in_transit',
  'ready_to_collect',
  'delivered',
  'exception',
  'failed',
  'returned',
];

/**
 * Status filter options for the merged listing: `NEW` (orders without any shipment)
 * followed by the colour-coding groups. The `new` group (a freshly-created shipment
 * with no carrier event yet) is intentionally folded under the `NEW` option.
 */
export const ORDER_STATUS_FILTERS: OrderStatusFilter[] = [
  ORDER_SHIPMENT_STATUS_NEW,
  'created',
  'in_transit',
  'ready_to_collect',
  'delivered',
  'exception',
  'failed',
  'returned',
];

type EventCodeEntry = { code: string; group: ShipmentStatusGroup };

/**
 * The full InPost tracking event catalogue (plus the synthetic `INTERNAL.NEW`) with
 * the colour-coding group each code belongs to. Source of truth for the event-code
 * filter dropdown. Kept in sync with the backend `TrackingEventCode::group()` mapping.
 */
export const TRACKING_EVENT_CODES: EventCodeEntry[] = [
  { code: 'INTERNAL.NEW', group: 'new' },

  { code: 'CC.001', group: 'in_transit' },
  { code: 'CC.002', group: 'in_transit' },
  { code: 'CC.003', group: 'exception' },

  { code: 'CRE.1001', group: 'created' },
  { code: 'CRE.1002', group: 'created' },

  { code: 'EOL.1001', group: 'delivered' },
  { code: 'EOL.1002', group: 'delivered' },
  { code: 'EOL.1003', group: 'delivered' },
  { code: 'EOL.1004', group: 'delivered' },
  { code: 'EOL.1005', group: 'delivered' },
  { code: 'EOL.1006', group: 'delivered' },
  { code: 'EOL.1007', group: 'delivered' },
  { code: 'EOL.1008', group: 'delivered' },

  { code: 'EOL.9001', group: 'failed' },
  { code: 'EOL.9002', group: 'failed' },
  { code: 'EOL.9003', group: 'failed' },
  { code: 'EOL.9004', group: 'failed' },
  { code: 'EOL.9005', group: 'failed' },
  { code: 'EOL.9006', group: 'failed' },

  { code: 'FMD.1001', group: 'in_transit' },
  { code: 'FMD.1002', group: 'in_transit' },
  { code: 'FMD.1003', group: 'in_transit' },
  { code: 'FMD.1004', group: 'in_transit' },
  { code: 'FMD.1005', group: 'in_transit' },
  { code: 'FMD.1006', group: 'in_transit' },
  { code: 'FMD.1007', group: 'in_transit' },
  { code: 'FMD.1008', group: 'exception' },
  { code: 'FMD.1009', group: 'exception' },
  { code: 'FMD.1010', group: 'exception' },
  { code: 'FMD.1011', group: 'exception' },
  { code: 'FMD.1012', group: 'exception' },
  { code: 'FMD.1013', group: 'in_transit' },
  { code: 'FMD.1014', group: 'exception' },
  { code: 'FMD.1015', group: 'in_transit' },
  { code: 'FMD.1016', group: 'exception' },
  { code: 'FMD.1017', group: 'exception' },
  { code: 'FMD.9001', group: 'exception' },
  { code: 'FMD.9002', group: 'exception' },

  { code: 'FUL.1001', group: 'in_transit' },
  { code: 'FUL.1002', group: 'in_transit' },
  { code: 'FUL.1003', group: 'in_transit' },

  { code: 'HAN.1001', group: 'in_transit' },

  { code: 'INF.1001', group: 'in_transit' },
  { code: 'INF.9001', group: 'exception' },
  { code: 'INF.9003', group: 'exception' },
  { code: 'INF.9004', group: 'exception' },
  { code: 'INF.9005', group: 'exception' },
  { code: 'INF.9009', group: 'exception' },
  { code: 'INF.9010', group: 'in_transit' },
  { code: 'INF.9012', group: 'in_transit' },
  { code: 'INF.9013', group: 'exception' },
  { code: 'INF.9014', group: 'in_transit' },
  { code: 'INF.9016', group: 'exception' },
  { code: 'INF.9017', group: 'exception' },

  { code: 'LMD.1001', group: 'in_transit' },
  { code: 'LMD.1002', group: 'in_transit' },
  { code: 'LMD.1003', group: 'ready_to_collect' },
  { code: 'LMD.1004', group: 'ready_to_collect' },
  { code: 'LMD.1005', group: 'ready_to_collect' },
  { code: 'LMD.1006', group: 'ready_to_collect' },
  { code: 'LMD.3001', group: 'exception' },
  { code: 'LMD.3002', group: 'exception' },
  { code: 'LMD.3003', group: 'exception' },
  { code: 'LMD.3004', group: 'exception' },
  { code: 'LMD.3005', group: 'exception' },
  { code: 'LMD.3006', group: 'exception' },
  { code: 'LMD.3007', group: 'exception' },
  { code: 'LMD.3008', group: 'exception' },
  { code: 'LMD.3009', group: 'exception' },
  { code: 'LMD.3010', group: 'exception' },
  { code: 'LMD.3011', group: 'exception' },
  { code: 'LMD.3012', group: 'exception' },
  { code: 'LMD.3013', group: 'exception' },
  { code: 'LMD.3014', group: 'exception' },
  { code: 'LMD.3015', group: 'exception' },
  { code: 'LMD.3016', group: 'exception' },
  { code: 'LMD.3017', group: 'exception' },
  { code: 'LMD.9001', group: 'exception' },
  { code: 'LMD.9002', group: 'exception' },
  { code: 'LMD.9003', group: 'exception' },
  { code: 'LMD.9004', group: 'exception' },
  { code: 'LMD.9005', group: 'exception' },
  { code: 'LMD.9006', group: 'exception' },
  { code: 'LMD.9007', group: 'exception' },
  { code: 'LMD.9008', group: 'exception' },
  { code: 'LMD.9009', group: 'exception' },
  { code: 'LMD.9010', group: 'exception' },
  { code: 'LMD.9011', group: 'exception' },
  { code: 'LMD.9012', group: 'exception' },
  { code: 'LMD.9013', group: 'exception' },
  { code: 'LMD.9014', group: 'returned' },
  { code: 'LMD.9015', group: 'exception' },
  { code: 'LMD.9016', group: 'exception' },
  { code: 'LMD.9017', group: 'exception' },
  { code: 'LMD.9018', group: 'exception' },
  { code: 'LMD.9019', group: 'exception' },
  { code: 'LMD.9020', group: 'exception' },
  { code: 'LMD.9021', group: 'exception' },
  { code: 'LMD.9022', group: 'exception' },
  { code: 'LMD.9023', group: 'exception' },
  { code: 'LMD.9024', group: 'exception' },
  { code: 'LMD.9025', group: 'exception' },
  { code: 'LMD.9026', group: 'exception' },
  { code: 'LMD.9027', group: 'exception' },
  { code: 'LMD.9028', group: 'exception' },
  { code: 'LMD.9029', group: 'exception' },
  { code: 'LMD.9030', group: 'exception' },

  { code: 'MMD.1001', group: 'in_transit' },
  { code: 'MMD.1002', group: 'in_transit' },
  { code: 'MMD.1003', group: 'in_transit' },
  { code: 'MMD.1004', group: 'in_transit' },
  { code: 'MMD.1005', group: 'in_transit' },
  { code: 'MMD.1006', group: 'exception' },
  { code: 'MMD.1007', group: 'in_transit' },
  { code: 'MMD.1009', group: 'in_transit' },
  { code: 'MMD.3001', group: 'in_transit' },
  { code: 'MMD.9001', group: 'exception' },

  { code: 'RTS.1001', group: 'returned' },
  { code: 'RTS.1002', group: 'returned' },
];

/** i18n key segment for an event code: dots are not valid i18next key separators. */
export function eventCodeKey(code: string): string {
  return code.replaceAll('.', '_');
}
